import { createFileRoute, Link } from "@tanstack/react-router";
import { Suspense } from "react";
import { ArrowRight, Phone, ShieldCheck, MapPin, CheckCircle2, Award, Wrench } from "lucide-react";
import { PageHeader } from "./fordon";
import { FyndanuGrid, FyndanuGridSkeleton, fyndanuListingsQuery } from "@/components/FyndanuGrid";
import { RAPIDO_MODELS } from "@/data/rapido-models";
import { PART_GUIDES, type PartGuide } from "@/data/rapido-part-guides";

/** Verkstadsguider grupperade per system — gör varje guide nåbar från hubben. */
const PART_GUIDE_SYSTEMS: Array<[string, PartGuide[]]> = Object.entries(
  PART_GUIDES.reduce<Record<string, PartGuide[]>>((acc, g) => {
    (acc[g.system] ??= []).push(g);
    return acc;
  }, {}),
).sort((a, b) => a[0].localeCompare(b[0], "sv"));

const URL_SELF = "https://www.akhusbilfritid.se/rapido";

const FAQ = [
  {
    q: "Vilken Rapido husbil ska jag välja?",
    a: "Kort svar: bo mycket och året runt → integrerad 9005 DFH eller halvintegrerad 866F. Långresor för par → plåthusbilarna V62 (fransk säng) eller V68 (enkelsängar). Stad och smidighet → V55. Alkovklassiker för familj → äldre 7-serien. Vi går igenom skillnaderna på plats i Kalix — du är alltid välkommen att titta innan du bestämmer dig.",
  },
  {
    q: "Varför är Rapido husbil dyrare än vissa konkurrenter?",
    a: "Rapido bygger fransk kvalitet — dubbla golv där det behövs, tystare kaross, bättre virke i inredningen och en montering som håller över tid. Det märks på begagnatmarknaden: en välskött Rapido har ovanligt låg värdeminskning jämfört med volymmärkena. Kostnaden per körd mil blir ofta lägre trots högre inköpspris.",
  },
  {
    q: "Vad ingår i garantin på en ny Rapido?",
    a: "Fabriksgaranti på karossen och installerade tekniska system, plus separat garanti från chassitillverkaren (Fiat/Citroën). Vi hjälper dig med all garantihantering — du behöver inte prata med Frankrike själv.",
  },
  {
    q: "Hur ofta ska en Rapido servas?",
    a: "Chassit följer Fiat/Citroën-intervallen (typiskt varje år eller 30 000 km). Uppbyggnaden — tätningar, taktätningar, gasol, värme — bör kollas årligen. Vi rekommenderar fukttest en gång per år; det är den enskilt viktigaste kontrollen för att husbilen ska hålla värdet.",
  },
  {
    q: "Kan ni hjälpa till med finansiering och inbyte?",
    a: "Ja. Vi samarbetar med etablerade finansbolag och tar in inbyten. Ring 070-862 97 43 eller mejla info@akhusbilfritid.se så tar vi det tillsammans.",
  },
  {
    q: "Är ni auktoriserad Rapido-återförsäljare?",
    a: "Ja. AK Husbil & Fritid är officiell Rapido-återförsäljare för norra Sverige. Vi säljer både nya Rapido från fabrik och kontrollerade begagnade — och vi har verkstadskompetens för märket.",
  },
];

export const Route = createFileRoute("/rapido/")({
  loader: ({ context }) => context.queryClient.ensureQueryData(fyndanuListingsQuery),
  head: () => ({
    meta: [
      { title: "Rapido husbil – Officiell återförsäljare | AK Husbil & Fritid" },
      {
        name: "description",
        content:
          "Rapido husbil hos AK Husbil & Fritid i Kalix. Officiell återförsäljare. V55, V62, V68, 866F, 9005 DFH — nya och begagnade. Ring 070-862 97 43.",
      },
      { name: "keywords", content: "rapido husbil, rapido återförsäljare, rapido sverige, rapido plåtis, rapido v62, rapido v68, rapido 866f, rapido 9005 dfh, begagnade rapido husbilar" },
      { property: "og:type", content: "website" },
      { property: "og:title", content: "Rapido husbil – Officiell återförsäljare | AK Husbil & Fritid" },
      { property: "og:description", content: "Officiell Rapido-återförsäljare i Kalix. Hela modellprogrammet — plåtis, halvintegrerad, integrerad." },
      { property: "og:url", content: URL_SELF },
    ],
    links: [{ rel: "canonical", href: URL_SELF }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Hem", item: "https://www.akhusbilfritid.se/" },
            { "@type": "ListItem", position: 2, name: "Rapido husbil", item: URL_SELF },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "AutoDealer",
          name: "AK Husbil & Fritid – Rapido återförsäljare",
          url: URL_SELF,
          brand: { "@type": "Brand", name: "Rapido" },
          areaServed: "SE",
          address: {
            "@type": "PostalAddress",
            streetAddress: "Stabsvägen 5",
            addressLocality: "Kalix",
            postalCode: "952 51",
            addressCountry: "SE",
          },
          telephone: "+46708629743",
        }),
      },
    ],
  }),
  component: RapidoHub,
});

function RapidoHub() {
  return (
    <>
      <PageHeader
        label="Officiell Rapido-återförsäljare · Kalix · Norra Sverige"
        title={<>Rapido husbil — <span className="font-italic-display text-accent">franskt bygge</span>, svensk vinter.</>}
        sub="Rapido bygger husbilar som håller. Dubbla golv där det behövs, tystare kaross, virke som inte spricker och en värdeminskning som är bland branschens lägsta. Vi är officiell återförsäljare — och vi kör själva Rapido genom Norrland året runt."
      />

      <section className="container-edge -mt-6 pb-4">
        <div className="flex flex-wrap gap-3">
          <Link to="/rapido/begagnade" className="inline-flex items-center gap-2 btn-shine rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90">
            Se begagnade Rapido <ArrowRight size={14} />
          </Link>
          <Link to="/rapido/prislista" className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-3 text-sm font-semibold transition hover:bg-secondary">
            Prislista 2024–2025
          </Link>
          <a href="tel:0708629743" className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-3 text-sm font-semibold transition hover:bg-secondary">
            <Phone size={16} /> 070-862 97 43
          </a>
        </div>
      </section>

      {/* Trust row */}
      <section className="container-edge py-12">
        <div className="grid gap-6 rounded-3xl border border-border bg-card p-8 md:grid-cols-3 md:p-10">
          <div>
            <Award className="text-accent" size={28} />
            <h3 className="mt-4 font-display text-2xl">Officiell återförsäljare</h3>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">Vi säljer nya Rapido direkt från fabrik i Frankrike — och står för garantihanteringen åt dig.</p>
          </div>
          <div>
            <Wrench className="text-accent" size={28} />
            <h3 className="mt-4 font-display text-2xl">Verkstad för märket</h3>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">Vi känner Rapidos konstruktion — vet var fuktproblemen brukar dyka upp och vad som ska förebyggas.</p>
          </div>
          <div>
            <ShieldCheck className="text-accent" size={28} />
            <h3 className="mt-4 font-display text-2xl">Kontrollerade begagnade</h3>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">Varje begagnad Rapido går igenom fukttest, chassikoll och funktionsprov innan den läggs ut till försäljning.</p>
          </div>
        </div>
      </section>

      {/* Modellprogram */}
      <section className="container-edge py-16 md:py-20">
        <p className="eyebrow">Modellprogrammet</p>
        <h2 className="mt-3 font-display text-4xl md:text-5xl">Hela Rapidos <span className="font-italic-display text-accent">modellutbud</span>.</h2>
        <p className="mt-6 max-w-3xl text-lg leading-relaxed text-muted-foreground">
          Rapido tillverkar plåthusbilar (V-serien / "plåtis"), halvintegrerade och integrerade. Varje modell har en tydlig karaktär — här är korta guider till de mest sålda.
        </p>

        <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {RAPIDO_MODELS.map((m) => (
            <Link
              key={m.slug}
              to="/rapido/modeller/$model"
              params={{ model: m.slug }}
              className="group rounded-3xl border border-border bg-card p-6 transition hover:-translate-y-1 hover:shadow-xl"
            >
              <p className="eyebrow">{m.category}</p>
              <h3 className="mt-3 font-display text-3xl">{m.name}</h3>
              <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{m.intro.slice(0, 140)}…</p>
              <div className="mt-6 flex flex-wrap gap-2 text-xs text-foreground/70">
                <span className="rounded-full bg-secondary px-3 py-1">{m.lengthCm} cm</span>
                <span className="rounded-full bg-secondary px-3 py-1">{m.totalWeightKg} kg</span>
                <span className="rounded-full bg-secondary px-3 py-1">Sover {m.sleeps.split("(")[0].trim()}</span>
              </div>
              <span className="mt-6 inline-flex items-center gap-1 text-sm font-semibold text-accent">
                Läs om {m.name} <ArrowRight size={14} className="transition group-hover:translate-x-0.5" />
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* Aktuellt lager */}
      <section className="bg-warm">
        <div className="container-edge py-16 md:py-24">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="eyebrow">Aktuellt lager</p>
              <h2 className="mt-3 font-display text-4xl md:text-5xl">Rapido i lager <span className="font-italic-display text-accent">just nu</span>.</h2>
              <p className="mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground">
                Live-flöde från vår Fyndanu-butik. Uppdateras dagligen — försvinner en bil härifrån är den ofta redan såld.
              </p>
            </div>
            <Link to="/rapido/begagnade" className="inline-flex items-center gap-2 text-sm font-semibold text-accent">
              Se alla begagnade Rapido <ArrowRight size={14} />
            </Link>
          </div>
          <div className="mt-10">
            <Suspense fallback={<FyndanuGridSkeleton />}>
              <FyndanuGrid />
            </Suspense>
          </div>
        </div>
      </section>

      {/* Verkstadsguider per system */}
      <section className="container-edge py-16 md:py-24">
        <p className="eyebrow">Verkstad & felsökning</p>
        <h2 className="mt-3 font-display text-4xl md:text-5xl">
          Rapido-guider <span className="font-italic-display text-accent">system för system</span>.
        </h2>
        <p className="mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground">
          Våra egna verkstadsguider — symptom, orsak, delnummer, tidsåtgång och prisläge. Skrivna
          utifrån jobb vi faktiskt gjort i Kalix.
        </p>
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {PART_GUIDE_SYSTEMS.map(([system, guides]) => (
            <div key={system} className="rounded-2xl border border-border bg-card p-5">
              <h3 className="font-display text-lg">{system}</h3>
              <ul className="mt-3 space-y-2">
                {guides.map((g) => (
                  <li key={g.slug}>
                    <Link
                      to="/rapido/guider/$slug"
                      params={{ slug: g.slug }}
                      className="text-sm leading-snug text-muted-foreground underline-offset-4 transition hover:text-accent hover:underline"
                    >
                      {g.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>


      {/* FAQ */}
      <section className="container-edge py-16 md:py-24">
        <p className="eyebrow">Vanliga frågor om Rapido</p>
        <h2 className="mt-3 font-display text-4xl md:text-5xl">
          Rapido husbil — <span className="font-italic-display text-accent">vad du behöver veta</span>.
        </h2>
        <div className="mt-10 grid gap-6 md:grid-cols-2">
          {FAQ.map((f) => (
            <div key={f.q} className="rounded-2xl border border-border bg-card p-6">
              <h3 className="font-display text-xl">{f.q}</h3>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="container-edge pb-20">
        <div className="rounded-3xl border border-border bg-foreground p-8 md:p-14 text-background">
          <p className="eyebrow" style={{ color: "hsl(var(--accent))" }}>Nästa steg</p>
          <h2 className="mt-3 font-display text-4xl md:text-6xl leading-[1.05]">Prova en Rapido innan du bestämmer dig.</h2>
          <p className="mt-6 max-w-xl text-base leading-relaxed opacity-80">
            Vi är i Kalix — ring så bokar vi en tid. Du får sitta i modellen du är nyfiken på och prata igenom det som spelar roll för just din användning.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="tel:0708629743" className="inline-flex items-center gap-2 rounded-full bg-accent px-6 py-3.5 text-sm font-semibold text-accent-foreground hover:opacity-90">
              <Phone size={16} /> Ring 070-862 97 43
            </a>
            <Link to="/kontakt" className="inline-flex items-center gap-2 rounded-full border border-background/30 px-6 py-3.5 text-sm font-semibold hover:bg-background/10">
              <MapPin size={16} /> Boka besök i Kalix
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
